import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import {
  assetsQuery,
  devicesQuery,
  gatewaysQuery,
  loanAgreementsQuery,
  serviceAgreementsQuery,
  siteQuery,
} from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDevEui } from "@/lib/format";
import { SiteEditDialog } from "./site-edit-dialog";

/**
 * Hub for one physical site: everything installed or contracted there,
 * linked when a detail route exists.
 */
export function SiteDetailPage() {
  const { t } = useTranslation("pages");
  const { id = "" } = useParams<{ id: string }>();
  const client = useApiClient();
  const { user } = useAuth();
  const canEdit = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const [editing, setEditing] = useState(false);

  const site = useQuery(siteQuery(client, id));
  const assets = useQuery({ ...assetsQuery(client, { page: 1, siteId: id }), enabled: Boolean(id) });
  const devices = useQuery({
    ...devicesQuery(client, { page: 1, siteId: id }),
    enabled: Boolean(id),
  });
  const gateways = useQuery({
    ...gatewaysQuery(client, { page: 1, siteId: id }),
    enabled: Boolean(id),
  });
  const loans = useQuery({
    ...loanAgreementsQuery(client, { page: 1, siteId: id }),
    enabled: Boolean(id),
  });
  const services = useQuery({
    ...serviceAgreementsQuery(client, { page: 1, siteId: id }),
    enabled: Boolean(id),
  });

  if (site.isPending) {
    return <LoadingBlock label={t("loadingSite")} />;
  }

  if (site.isError) {
    return (
      <>
        <PageHeader title={t("siteDetailFallback")} />
        <ErrorBlock error={site.error} onRetry={() => void site.refetch()} />
        <p className="mt-3 text-sm">
          <Link to="/sites" className="text-brand-strong underline underline-offset-2">
            {t("backToSites")}
          </Link>
        </p>
      </>
    );
  }

  const data = site.data;
  const addressLine = data.address?.trim() ? data.address : EM_DASH;

  return (
    <>
      <PageHeader
        title={data.name}
        description={addressLine}
        action={
          canEdit ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t("editSite")}
            </Button>
          ) : null
        }
      />

      <p className="mb-6 text-sm">
        <Link to="/sites" className="text-brand-strong underline underline-offset-2">
          {t("backToSites")}
        </Link>
      </p>

      <div className="flex flex-col gap-8">
        <HubSection
          title={t("siteSectionAssets")}
          loading={assets.isPending}
          error={assets.error}
          onRetry={() => void assets.refetch()}
          empty={t("siteSectionAssetsEmpty")}
          items={(assets.data?.data ?? []).map((asset) => ({
            key: asset.id,
            label: asset.name,
          }))}
        />

        <HubSection
          title={t("siteSectionDevices")}
          loading={devices.isPending}
          error={devices.error}
          onRetry={() => void devices.refetch()}
          empty={t("siteSectionDevicesEmpty")}
          items={(devices.data?.data ?? []).map((device) => ({
            key: device.id,
            label: `${formatDevEui(device.devEui)} · ${device.manufacturer} ${device.model}`,
            to: `/devices/${device.id}`,
          }))}
        />

        <HubSection
          title={t("siteSectionGateways")}
          loading={gateways.isPending}
          error={gateways.error}
          onRetry={() => void gateways.refetch()}
          empty={t("siteSectionGatewaysEmpty")}
          items={(gateways.data?.data ?? []).map((gateway) => ({
            key: gateway.id,
            label: gateway.name,
          }))}
        />

        <HubSection
          title={t("siteSectionComodatos")}
          loading={loans.isPending}
          error={loans.error}
          onRetry={() => void loans.refetch()}
          empty={t("siteSectionComodatosEmpty")}
          items={(loans.data?.data ?? []).map((loan) => ({
            key: loan.id,
            label: loan.number,
            to: `/loan-agreements/${loan.id}`,
          }))}
        />

        <HubSection
          title={t("siteSectionServices")}
          loading={services.isPending}
          error={services.error}
          onRetry={() => void services.refetch()}
          empty={t("siteSectionServicesEmpty")}
          items={(services.data?.data ?? []).map((service) => ({
            key: service.id,
            label: service.number,
            to: `/service-agreements/${service.id}`,
          }))}
        />
      </div>

      {canEdit ? (
        <SiteEditDialog open={editing} site={data} onClose={() => setEditing(false)} />
      ) : null}
    </>
  );
}

function HubSection({
  title,
  loading,
  error,
  onRetry,
  empty,
  items,
}: {
  title: string;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  empty: string;
  items: readonly { key: string; label: string; to?: string }[];
}) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-ink">{title}</h2>

      {loading ? <LoadingBlock /> : null}

      {error ? <ErrorBlock error={error} onRetry={onRetry} /> : null}

      {!loading && !error && items.length === 0 ? (
        <p className="text-sm text-ink-muted">{empty}</p>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <ul className="list-inside list-disc text-sm text-ink">
          {items.map((item) => (
            <li key={item.key}>
              {item.to ? (
                <Link to={item.to} className="text-brand underline underline-offset-2">
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
